import { useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Upload, Plus, LayoutGrid, Loader2, Group, Sparkles, FileText, ClipboardCheck, BookOpen, ChevronDown, Presentation, Download, Menu, MessageSquareText, MonitorSmartphone, MoreVertical, MoreHorizontal, Check, HelpCircle, ChevronLeft, ChevronRight, PanelLeftOpen, PanelRightOpen, Eye, EyeOff, LifeBuoy } from "lucide-react";
import { toPng } from "html-to-image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { ModelTree } from "@/components/sidebar/ModelTree";
import { CreateElementDialog } from "@/components/sidebar/CreateElementDialog";
import { CreateRelationshipDialog } from "@/components/sidebar/CreateRelationshipDialog";
import { ElementDetailsDialog } from "@/components/sidebar/ElementDetailsDialog";
import { ViewsTabs } from "@/components/ViewsTabs";
import { ArchiCanvasWrapper } from "@/components/canvas/ArchiCanvas";
import { SimpleViewWrapper } from "@/components/canvas/SimpleView";
import { AiDock } from "@/components/ai/AiDock";
import { GroupSuggestionsDialog } from "@/components/ai/GroupSuggestionsDialog";
import { ImportPanel } from "@/components/import/ImportPanel";
import { HelpCenter } from "@/components/help/HelpCenter";
import { SupportCenter } from "@/components/support/SupportCenter";
import { api } from "@/api/client";
import { downloadBase64File } from "@/lib/download";
import { toast } from "sonner";

function loadWorkbenchLayoutPrefs(projectId) {
  try {
    const raw = localStorage.getItem(`archi_workbench_layout_${projectId}`);
    const prefs = raw ? JSON.parse(raw) : {};
    return {
      treeCollapsed: !!prefs.treeCollapsed,
      chatCollapsed: !!prefs.chatCollapsed,
      keyVisible: prefs.keyVisible !== false,
    };
  } catch (e) {
    return { treeCollapsed: false, chatCollapsed: false, keyVisible: true };
  }
}

export default function Workbench() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialViewId = location.state?.initialViewId;
  const isMobile = useIsMobile();
  const [mobileTreeOpen, setMobileTreeOpen] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [project, setProject] = useState(null);
  const [elements, setElements] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [views, setViews] = useState([]);
  const [owners, setOwners] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [activeView, setActiveView] = useState(null);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [focusRequest, setFocusRequest] = useState(null);
  const [createElementOpen, setCreateElementOpen] = useState(false);
  const [createElementLayer, setCreateElementLayer] = useState("business");
  const [pendingConnection, setPendingConnection] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [layoutLoading, setLayoutLoading] = useState(false);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportStep, setReportStep] = useState("");
  const [treeCheckedIds, setTreeCheckedIds] = useState([]);
  const [simpleViewMode, setSimpleViewMode] = useState(false);
  const [simpleViewData, setSimpleViewData] = useState(null);
  const [simpleViewLoading, setSimpleViewLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [treeCollapsed, setTreeCollapsed] = useState(() => loadWorkbenchLayoutPrefs(projectId).treeCollapsed);
  const [chatCollapsed, setChatCollapsed] = useState(() => loadWorkbenchLayoutPrefs(projectId).chatCollapsed);
  const [keyVisible, setKeyVisible] = useState(() => loadWorkbenchLayoutPrefs(projectId).keyVisible);
  const [hiddenToolbarIds, setHiddenToolbarIds] = useState(() => new Set());
  const [compactEdges, setCompactEdges] = useState(false);
  const isCompactEdgesRef = useRef(false);
  const actionRowRef = useRef(null);
  const toolbarOuterRowRef = useRef(null);
  const toolbarItemRefs = useRef({});
  const activeViewRef = useRef(activeView);

  const elementsById = useMemo(() => Object.fromEntries(elements.map((e) => [e.id, e])), [elements]);

  const ungroupedCount = useMemo(() => {
    if (!activeView) return 0;
    return (activeView.nodes || []).filter((n) => !n.parent_node_id && elementsById[n.element_id]?.type !== "grouping").length;
  }, [activeView, elementsById]);

  const loadProject = useCallback(async () => {
    const res = await api.get(`/projects/${projectId}`);
    setProject(res.data);
  }, [projectId]);

  const loadElements = useCallback(async () => {
    const res = await api.get(`/projects/${projectId}/elements`);
    setElements(res.data);
  }, [projectId]);

  const loadRelationships = useCallback(async () => {
    const res = await api.get(`/projects/${projectId}/relationships`);
    setRelationships(res.data);
  }, [projectId]);

  const loadOwners = useCallback(async () => {
    const res = await api.get(`/projects/${projectId}/owners`);
    setOwners(res.data);
  }, [projectId]);

  const loadViews = useCallback(async (autoSelect) => {
    const res = await api.get(`/projects/${projectId}/views`);
    setViews(res.data);
    if (autoSelect && res.data.length) {
      setActiveViewId((prev) => prev || res.data[0].id);
    }
    return res.data;
  }, [projectId]);

  const loadActiveView = useCallback(
    async (viewId) => {
      if (!viewId) {
        setActiveView(null);
        return;
      }
      const res = await api.get(`/projects/${projectId}/views/${viewId}`);
      setActiveView(res.data);
    },
    [projectId]
  );

  useEffect(() => {
    loadProject();
    loadElements();
    loadRelationships();
    loadOwners();
    loadViews(true).then((viewsList) => {
      if (initialViewId && viewsList.some((v) => v.id === initialViewId)) {
        setActiveViewId(initialViewId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const prefs = loadWorkbenchLayoutPrefs(projectId);
    setTreeCollapsed(prefs.treeCollapsed);
    setChatCollapsed(prefs.chatCollapsed);
    setKeyVisible(prefs.keyVisible);
  }, [projectId]);

  useEffect(() => {
    localStorage.setItem(
      `archi_workbench_layout_${projectId}`,
      JSON.stringify({ treeCollapsed, chatCollapsed, keyVisible })
    );
  }, [projectId, treeCollapsed, chatCollapsed, keyVisible]);

  useEffect(() => {
    if (activeViewId) loadActiveView(activeViewId);
  }, [activeViewId, loadActiveView]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadElements(), loadRelationships(), loadViews(true)]);
    if (activeViewId) loadActiveView(activeViewId);
  }, [loadElements, loadRelationships, loadViews, activeViewId, loadActiveView]);

  const handleCreateElement = async ({ type, name, documentation }) => {
    await api.post(`/projects/${projectId}/elements`, { type, name, documentation, view_id: activeViewId });
    await loadElements();
    if (activeViewId) await loadActiveView(activeViewId);
    toast.success(`Created "${name}"`);
  };

  const handleAddElementFromTree = async (elementId) => {
    if (!activeViewId) {
      toast.error("Create a view first");
      return;
    }
    await api.post(`/projects/${projectId}/views/${activeViewId}/nodes`, { element_id: elementId });
    await loadActiveView(activeViewId);
  };

  const handleNodeMove = async (nodeId, x, y) => {
    await api.put(`/projects/${projectId}/views/${activeViewId}/nodes/${nodeId}`, { x, y });
  };

  const handleRemoveNode = async (nodeId) => {
    await api.delete(`/projects/${projectId}/views/${activeViewId}/nodes/${nodeId}`);
    await loadActiveView(activeViewId);
  };

  const handleConnectElements = (sourceId, targetId) => {
    setPendingConnection({ source: elementsById[sourceId], target: elementsById[targetId] });
  };

  const handleCreateRelationship = async ({ type, name }) => {
    await api.post(`/projects/${projectId}/relationships`, {
      type,
      source_id: pendingConnection.source.id,
      target_id: pendingConnection.target.id,
      name,
    });
    await loadRelationships();
    await loadActiveView(activeViewId);
  };

  const handleUpdateRelationship = async (relationshipId, fields) => {
    await api.put(`/projects/${projectId}/relationships/${relationshipId}`, fields);
    await loadRelationships();
    await loadActiveView(activeViewId);
  };

  const handleDeleteRelationship = async (relationshipId) => {
    await api.delete(`/projects/${projectId}/relationships/${relationshipId}`);
    await loadRelationships();
    await loadActiveView(activeViewId);
  };

  const handleSaveElement = async (elementId, fields) => {
    await api.put(`/projects/${projectId}/elements/${elementId}`, fields);
    await Promise.all([loadElements(), loadOwners()]);
  };

  const handleDeleteElement = async (elementId) => {
    await api.delete(`/projects/${projectId}/elements/${elementId}`);
    setSelectedElementId(null);
    await refreshAll();
    toast.success("Element deleted");
  };

  const handleCreateView = async (name) => {
    const res = await api.post(`/projects/${projectId}/views`, { name });
    await loadViews(false);
    setActiveViewId(res.data.id);
  };

  const handleRenameView = async (viewId, newName) => {
    try {
      await api.put(`/projects/${projectId}/views/${viewId}`, { name: newName });
      await loadViews(false);
      if (activeViewId === viewId) setActiveView((v) => (v ? { ...v, name: newName } : v));
      toast.success("View renamed");
    } catch (e) {
      toast.error("Could not rename view");
    }
  };

  const handleDeleteView = async (viewId) => {
    try {
      await api.delete(`/projects/${projectId}/views/${viewId}`);
      const remaining = await loadViews(false);
      if (activeViewId === viewId) setActiveViewId(remaining[0]?.id || null);
      toast.success("View deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete view");
    }
  };

  const handleToggleTreeChecked = (elementId) => {
    setTreeCheckedIds((prev) => (prev.includes(elementId) ? prev.filter((id) => id !== elementId) : [...prev, elementId]));
  };

  const handleClearTreeChecked = () => setTreeCheckedIds([]);

  const handleCreateViewFromChecked = async (name) => {
    const elementIds = treeCheckedIds;
    try {
      const res = await api.post(`/projects/${projectId}/views/from-selection`, { name, element_ids: elementIds });
      await loadViews(false);
      setActiveViewId(res.data.id);
      setTreeCheckedIds([]);
      toast.success(`Created view "${name}" with ${elementIds.length} element(s)`);
    } catch (e) {
      toast.error("Could not create view from selection");
    }
  };

  const handleAutoLayout = async () => {
    if (!activeViewId || !activeView?.nodes?.length) return;
    setLayoutLoading(true);
    try {
      const res = await api.post(`/projects/${projectId}/views/${activeViewId}/auto-layout`);
      setActiveView(res.data);
      toast.success("Diagram tidied up by layer");
    } catch (e) {
      toast.error("Could not auto-layout this view");
    } finally {
      setLayoutLoading(false);
    }
  };

  const handleLineStyleChange = async (lineStyle) => {
    if (!activeViewId) return;
    setActiveView((v) => (v ? { ...v, line_style: lineStyle } : v));
    try {
      await api.put(`/projects/${projectId}/views/${activeViewId}`, { line_style: lineStyle });
    } catch (e) {
      toast.error("Could not update line style");
    }
  };

  const handleNodeResize = async (nodeId, width, height) => {
    await api.put(`/projects/${projectId}/views/${activeViewId}/nodes/${nodeId}`, { width, height });
    await loadActiveView(activeViewId);
  };

  const handleNodeReparent = async (nodeId, parentNodeId, x, y) => {
    await api.put(`/projects/${projectId}/views/${activeViewId}/nodes/${nodeId}`, { parent_node_id: parentNodeId, x, y });
    await loadActiveView(activeViewId);
  };

  const handleToggleCollapse = async (nodeId, collapsed) => {
    await api.put(`/projects/${projectId}/views/${activeViewId}/nodes/${nodeId}`, { collapsed });
    await loadActiveView(activeViewId);
  };

  const handleGroupSelected = async () => {
    if (selectedNodeIds.length < 2) return;
    try {
      const res = await api.post(`/projects/${projectId}/views/${activeViewId}/group`, {
        node_ids: selectedNodeIds,
        name: groupName.trim() || "Group",
      });
      setActiveView(res.data);
      await loadElements();
      setSelectedNodeIds([]);
      setGroupDialogOpen(false);
      setGroupName("");
      toast.success("Grouped selected elements");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not group selected elements");
    }
  };

  const handleUngroup = async (groupNodeId) => {
    try {
      const res = await api.post(`/projects/${projectId}/views/${activeViewId}/ungroup/${groupNodeId}`);
      setActiveView(res.data);
      await loadElements();
      toast.success("Ungrouped");
    } catch (e) {
      toast.error("Could not ungroup");
    }
  };

  const handleSuggestGroupings = async () => {
    if (!activeViewId) return;
    setSuggestLoading(true);
    try {
      const res = await api.post(`/projects/${projectId}/views/${activeViewId}/suggest-groupings`);
      if (!res.data.suggestions?.length) {
        toast.info("No obvious groupings found in this view");
      } else {
        setGroupSuggestions(res.data.suggestions);
        setSuggestionsOpen(true);
      }
    } catch (e) {
      toast.error("Could not generate grouping suggestions");
    } finally {
      setSuggestLoading(false);
    }
  };

  const applyGroupSuggestion = async (suggestion, viewSnapshot) => {
    const view = viewSnapshot || activeView;
    const nodeIds = (view.nodes || [])
      .filter((n) => suggestion.element_ids.includes(n.element_id) && !n.parent_node_id)
      .map((n) => n.node_id);
    if (nodeIds.length < 2) return null;
    const res = await api.post(`/projects/${projectId}/views/${activeViewId}/group`, {
      node_ids: nodeIds,
      name: suggestion.name,
      auto_spread: true,
    });
    setActiveView(res.data);
    return res.data;
  };

  const handleAcceptSuggestion = async (index) => {
    const s = groupSuggestions[index];
    const result = await applyGroupSuggestion(s);
    setGroupSuggestions((arr) => arr.filter((_, i) => i !== index));
    if (result) {
      await loadElements();
      toast.success(`Grouped "${s.name}"`);
    } else {
      toast.error("Those elements are no longer available to group");
    }
  };

  const handleRejectSuggestion = (index) => {
    setGroupSuggestions((arr) => arr.filter((_, i) => i !== index));
  };

  const handleAcceptAllSuggestions = async () => {
    let view = activeView;
    for (const s of groupSuggestions) {
      const result = await applyGroupSuggestion(s, view);
      if (result) view = result;
    }
    await loadElements();
    setGroupSuggestions([]);
    toast.success("Applied all suggested groupings");
  };

  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);

  const waitForView = async (viewId, timeout = 4000) => {
    const start = Date.now();
    while (activeViewRef.current?.id !== viewId) {
      if (Date.now() - start > timeout) break;
      await new Promise((r) => setTimeout(r, 100));
    }
    await new Promise((r) => setTimeout(r, 500));
  };

  const captureViewImages = async () => {
    const originalViewId = activeViewId;
    const viewImages = {};
    for (let i = 0; i < views.length; i++) {
      const v = views[i];
      setReportStep(`Capturing view ${i + 1} of ${views.length}: ${v.name}`);
      setActiveViewId(v.id);
      await waitForView(v.id);
      const node = document.querySelector('[data-testid="archimate-canvas"] .react-flow__viewport');
      if (node) {
        try {
          const dataUrl = await toPng(node, { backgroundColor: "#F8FAFC", pixelRatio: 1.5 });
          viewImages[v.id] = dataUrl.split(",")[1];
        } catch (e) {
          // skip this view's diagram image if capture fails
        }
      }
    }
    if (originalViewId) {
      setActiveViewId(originalViewId);
      await waitForView(originalViewId);
    }
    return viewImages;
  };

  const handleGenerateReport = async () => {
    setReportGenerating(true);
    try {
      const viewImages = await captureViewImages();
      setReportStep("Writing executive summary with AI...");
      const res = await api.post(`/projects/${projectId}/report`, { view_images: viewImages });
      downloadBase64File(res.data.pdf_base64, `${res.data.filename_prefix}.pdf`, "application/pdf");
      downloadBase64File(
        res.data.docx_base64,
        `${res.data.filename_prefix}.docx`,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );
      toast.success("Report generated - PDF and Word files downloaded");
    } catch (e) {
      toast.error("Could not generate report");
    } finally {
      setReportGenerating(false);
      setReportStep("");
    }
  };

  const handleGenerateGapReport = async () => {
    setReportGenerating(true);
    setReportStep("Analyzing model for gaps...");
    try {
      const res = await api.post(`/projects/${projectId}/report/gap-analysis`);
      downloadBase64File(res.data.pdf_base64, `${res.data.filename_prefix}.pdf`, "application/pdf");
      downloadBase64File(
        res.data.docx_base64,
        `${res.data.filename_prefix}.docx`,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );
      toast.success("Gap analysis report generated - PDF and Word files downloaded");
    } catch (e) {
      toast.error("Could not generate gap analysis report");
    } finally {
      setReportGenerating(false);
      setReportStep("");
    }
  };

  const handleGenerateHld = async () => {
    setReportGenerating(true);
    try {
      const viewImages = await captureViewImages();
      setReportStep("Writing High-Level Design with AI...");
      const res = await api.post(`/projects/${projectId}/report/hld`, { view_images: viewImages });
      downloadBase64File(res.data.pdf_base64, `${res.data.filename_prefix}.pdf`, "application/pdf");
      downloadBase64File(
        res.data.docx_base64,
        `${res.data.filename_prefix}.docx`,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );
      toast.success("High-Level Design generated - PDF and Word files downloaded");
    } catch (e) {
      toast.error("Could not generate the High-Level Design");
    } finally {
      setReportGenerating(false);
      setReportStep("");
    }
  };

  const handleToggleSimpleView = async () => {
    if (simpleViewMode) {
      setSimpleViewMode(false);
      return;
    }
    setSimpleViewLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/simple-view`);
      setSimpleViewData(res.data);
      setSimpleViewMode(true);
    } catch (e) {
      toast.error("Could not generate Simple View");
    } finally {
      setSimpleViewLoading(false);
    }
  };

  const handleDownloadSimpleView = async () => {
    const node = document.querySelector('[data-testid="simple-view-canvas"] .react-flow__viewport');
    if (!node) return;
    try {
      const dataUrl = await toPng(node, { backgroundColor: "#F8FAFC", pixelRatio: 2, skipFonts: true });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${project.name.replace(/\s+/g, "-").toLowerCase()}-simple-view.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Simple View image downloaded");
    } catch (e) {
      toast.error("Could not export Simple View image");
    }
  };

  const selectedElement = selectedElementId ? elementsById[selectedElementId] : null;
  const selectedElementRelationships = selectedElement
    ? relationships.filter((r) => r.source_id === selectedElement.id || r.target_id === selectedElement.id)
    : [];

  const handleFocusElementOnCanvas = (elementId) => {
    const inView = (activeView?.nodes || []).some((n) => n.element_id === elementId);
    if (!inView) {
      toast.info("That element isn't in the current view");
      return;
    }
    setFocusRequest({ elementId, nonce: Date.now() });
    setSelectedElementId(null);
  };

  const setToolbarItemRef = (id) => (el) => {
    toolbarItemRefs.current[id] = el;
  };

  const toolbarFoldCandidates = [
    {
      id: "tidyUp",
      renderButton: (hidden) => (
        <Button
          key="tidyUp"
          ref={setToolbarItemRef("tidyUp")}
          size="sm"
          variant="outline"
          data-testid="auto-layout-button"
          onClick={handleAutoLayout}
          disabled={layoutLoading || !activeView?.nodes?.length}
          tabIndex={hidden ? -1 : undefined}
          className={`shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900 disabled:text-slate-400 disabled:bg-slate-50 ${
            hidden ? "invisible absolute pointer-events-none" : ""
          }`}
        >
          {layoutLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <LayoutGrid size={13} className="mr-1.5" />}
          Tidy Up
        </Button>
      ),
      renderMenuItem: () => (
        <DropdownMenuItem
          key="tidyUp-menu"
          data-testid="more-menu-tidy-up-item"
          onClick={handleAutoLayout}
          disabled={layoutLoading || !activeView?.nodes?.length}
        >
          <LayoutGrid size={13} className="mr-2" /> Tidy Up
        </DropdownMenuItem>
      ),
    },
    {
      id: "suggestGroupings",
      renderButton: (hidden) => (
        <div
          key="suggestGroupings"
          ref={setToolbarItemRef("suggestGroupings")}
          className={`relative shrink-0 ${hidden ? "invisible absolute pointer-events-none" : ""}`}
        >
          <Button
            size="sm"
            variant="outline"
            data-testid="suggest-groupings-button"
            onClick={handleSuggestGroupings}
            disabled={suggestLoading || !activeViewId}
            tabIndex={hidden ? -1 : undefined}
            className={`bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900 disabled:text-slate-400 disabled:bg-slate-50 ${
              ungroupedCount >= 6 ? "border-purple-300 ring-1 ring-purple-300" : ""
            }`}
          >
            {suggestLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Sparkles size={13} className="mr-1.5" />}
            Suggest Groupings
          </Button>
          {ungroupedCount >= 6 && (
            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5" data-testid="suggest-groupings-nudge-dot">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-purple-500" />
            </span>
          )}
        </div>
      ),
      renderMenuItem: () => (
        <DropdownMenuItem
          key="suggestGroupings-menu"
          data-testid="more-menu-suggest-groupings-item"
          onClick={handleSuggestGroupings}
          disabled={suggestLoading || !activeViewId}
        >
          <Sparkles size={13} className="mr-2" /> Suggest Groupings
          {ungroupedCount >= 6 && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-purple-500 shrink-0" />}
        </DropdownMenuItem>
      ),
    },
    {
      id: "lineStyle",
      renderButton: (hidden) => (
        <div
          key="lineStyle"
          ref={setToolbarItemRef("lineStyle")}
          className={`shrink-0 ${hidden ? "invisible absolute pointer-events-none" : ""}`}
        >
          <Select value={activeView?.line_style || "curved"} onValueChange={handleLineStyleChange} disabled={!activeViewId}>
            <SelectTrigger
              data-testid="line-style-select"
              tabIndex={hidden ? -1 : undefined}
              className="h-8 w-[128px] text-xs bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="curved" data-testid="line-style-option-curved">
                Curved Lines
              </SelectItem>
              <SelectItem value="straight" data-testid="line-style-option-straight">
                Straight Lines
              </SelectItem>
              <SelectItem value="orthogonal" data-testid="line-style-option-orthogonal">
                Manhattan Lines
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      ),
      renderMenuItem: () => (
        <div key="lineStyle-menu">
          <DropdownMenuItem data-testid="more-menu-line-style-curved" onClick={() => handleLineStyleChange("curved")}>
            {(activeView?.line_style || "curved") === "curved" ? <Check size={13} className="mr-2" /> : <span className="mr-2 w-[13px]" />} Curved Lines
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="more-menu-line-style-straight" onClick={() => handleLineStyleChange("straight")}>
            {activeView?.line_style === "straight" ? <Check size={13} className="mr-2" /> : <span className="mr-2 w-[13px]" />} Straight Lines
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="more-menu-line-style-orthogonal" onClick={() => handleLineStyleChange("orthogonal")}>
            {activeView?.line_style === "orthogonal" ? <Check size={13} className="mr-2" /> : <span className="mr-2 w-[13px]" />} Manhattan Lines
          </DropdownMenuItem>
        </div>
      ),
    },
    ...(selectedNodeIds.length >= 2
      ? [
          {
            id: "groupSelected",
            renderButton: (hidden) => (
              <Button
                key="groupSelected"
                ref={setToolbarItemRef("groupSelected")}
                size="sm"
                variant="outline"
                data-testid="group-selected-button"
                onClick={() => setGroupDialogOpen(true)}
                tabIndex={hidden ? -1 : undefined}
                className={`shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900 ${
                  hidden ? "invisible absolute pointer-events-none" : ""
                }`}
              >
                <Group size={13} className="mr-1.5" />
                Group Selected ({selectedNodeIds.length})
              </Button>
            ),
            renderMenuItem: () => (
              <DropdownMenuItem key="groupSelected-menu" data-testid="more-menu-group-selected-item" onClick={() => setGroupDialogOpen(true)}>
                <Group size={13} className="mr-2" /> Group Selected ({selectedNodeIds.length})
              </DropdownMenuItem>
            ),
          },
        ]
      : []),
    {
      id: "import",
      renderButton: (hidden) => (
        <Button
          key="import"
          ref={setToolbarItemRef("import")}
          size="sm"
          variant="outline"
          data-testid="open-import-button"
          onClick={() => setImportOpen(true)}
          tabIndex={hidden ? -1 : undefined}
          className={`shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900 ${
            hidden ? "invisible absolute pointer-events-none" : ""
          }`}
        >
          <Upload size={13} className="mr-1.5" />
          Import
        </Button>
      ),
      renderMenuItem: () => (
        <DropdownMenuItem key="import-menu" data-testid="more-menu-import-item" onClick={() => setImportOpen(true)}>
          <Upload size={13} className="mr-2" /> Import
        </DropdownMenuItem>
      ),
    },
    {
      id: "reports",
      renderButton: (hidden) => (
        <DropdownMenu key="reports">
          <DropdownMenuTrigger asChild>
            <Button
              ref={setToolbarItemRef("reports")}
              size="sm"
              variant="outline"
              data-testid="reports-menu-button"
              disabled={reportGenerating}
              tabIndex={hidden ? -1 : undefined}
              className={`shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900 disabled:text-slate-400 disabled:bg-slate-50 ${
                hidden ? "invisible absolute pointer-events-none" : ""
              }`}
            >
              {reportGenerating ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <FileText size={13} className="mr-1.5" />}
              Reports
              <ChevronDown size={13} className="ml-1.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem data-testid="generate-report-menu-item" onClick={handleGenerateReport} disabled={reportGenerating}>
              <FileText size={13} className="mr-2" /> Standard Report
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="generate-gap-report-menu-item" onClick={handleGenerateGapReport} disabled={reportGenerating}>
              <ClipboardCheck size={13} className="mr-2" /> Gap Analysis
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="generate-hld-menu-item" onClick={handleGenerateHld} disabled={reportGenerating}>
              <BookOpen size={13} className="mr-2" /> High-Level Design (HLD)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      renderMenuItem: () => (
        <div key="reports-menu">
          <DropdownMenuItem data-testid="more-menu-generate-report-item" onClick={handleGenerateReport} disabled={reportGenerating}>
            <FileText size={13} className="mr-2" /> Standard Report
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="more-menu-generate-gap-report-item" onClick={handleGenerateGapReport} disabled={reportGenerating}>
            <ClipboardCheck size={13} className="mr-2" /> Gap Analysis
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="more-menu-generate-hld-item" onClick={handleGenerateHld} disabled={reportGenerating}>
            <BookOpen size={13} className="mr-2" /> High-Level Design (HLD)
          </DropdownMenuItem>
        </div>
      ),
    },
    {
      id: "simpleView",
      renderButton: (hidden) => (
        <Button
          key="simpleView"
          ref={setToolbarItemRef("simpleView")}
          size="sm"
          variant="outline"
          data-testid="toggle-simple-view-button"
          onClick={handleToggleSimpleView}
          disabled={simpleViewLoading}
          tabIndex={hidden ? -1 : undefined}
          className={`shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900 disabled:text-slate-400 disabled:bg-slate-50 ${
            hidden ? "invisible absolute pointer-events-none" : ""
          }`}
        >
          {simpleViewLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Presentation size={13} className="mr-1.5" />}
          Simple View
        </Button>
      ),
      renderMenuItem: () => (
        <DropdownMenuItem key="simpleView-menu" data-testid="more-menu-simple-view-item" onClick={handleToggleSimpleView} disabled={simpleViewLoading}>
          <Presentation size={13} className="mr-2" /> Simple View
        </DropdownMenuItem>
      ),
    },
  ];

  useLayoutEffect(() => {
    if (isMobile || simpleViewMode) return;
    const container = actionRowRef.current;
    const outerRow = toolbarOuterRowRef.current;
    if (!container || !outerRow) return;
    const GAP = 8;
    const DIVIDER = 17;

    const recompute = () => {
      const containerWidth = container.clientWidth;
      const moreEl = toolbarItemRefs.current.moreButtonClone;
      const moreButtonWidth = (moreEl ? moreEl.offsetWidth : 56) + GAP;

      let total = 0;
      const widths = {};
      toolbarFoldCandidates.forEach((item) => {
        const el = toolbarItemRefs.current[item.id];
        const w = el ? el.offsetWidth + GAP : 0;
        widths[item.id] = w;
        total += w;
      });

      let hidden = new Set();
      if (total > containerWidth) {
        let running = total;
        for (const item of toolbarFoldCandidates) {
          if (running + moreButtonWidth <= containerWidth) break;
          hidden.add(item.id);
          running -= widths[item.id];
        }
      }
      setHiddenToolbarIds(hidden);

      const keyEl = toolbarItemRefs.current.key;
      const elementEl = toolbarItemRefs.current.element;
      const keyWidth = keyEl ? keyEl.offsetWidth : 0;
      const elementWidth = elementEl ? elementEl.offsetWidth : 0;
      const minRequired = keyWidth + DIVIDER + (hidden.size > 0 ? moreButtonWidth : 0) + DIVIDER + elementWidth;
      const buffer = isCompactEdgesRef.current ? 24 : 0;
      const nextCompact = outerRow.clientWidth < minRequired + buffer;
      if (nextCompact !== isCompactEdgesRef.current) {
        isCompactEdgesRef.current = nextCompact;
        setCompactEdges(nextCompact);
      }
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(container);
    ro.observe(outerRow);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, simpleViewMode, selectedNodeIds.length, ungroupedCount, activeViewId]);

  if (!project) {
    return <div className="h-screen flex items-center justify-center bg-slate-900 text-slate-400 text-sm">Loading project...</div>;
  }

  const treeContent = (
    <ModelTree
      elements={elements}
      selectedElementId={selectedElementId}
      onSelectElement={(id) => {
        setSelectedElementId(id);
        if (isMobile) setMobileTreeOpen(false);
      }}
      onAddElement={(layer) => {
        setCreateElementLayer(layer);
        setCreateElementOpen(true);
      }}
      onAddToView={handleAddElementFromTree}
      checkedIds={treeCheckedIds}
      onToggleChecked={handleToggleTreeChecked}
      onClearChecked={handleClearTreeChecked}
      onCreateViewFromChecked={handleCreateViewFromChecked}
      readOnly={isMobile}
    />
  );

  const chatContent = <AiDock projectId={projectId} currentView={activeView} onModelChanged={refreshAll} />;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-900 text-slate-100" data-testid="workbench-page">
      {!isMobile && !treeCollapsed && (
        <div className="w-72 flex-shrink-0 border-r border-slate-800 flex flex-col relative">
          <div className="h-12 flex items-center gap-2 px-3 border-b border-slate-800">
            <button data-testid="back-to-dashboard-button" onClick={() => navigate("/dashboard")} className="text-slate-400 hover:text-white">
              <ArrowLeft size={16} />
            </button>
            <span className="text-sm font-semibold truncate flex-1">{project.name}</span>
            <button
              data-testid="open-help-center-button"
              onClick={() => setHelpOpen(true)}
              className="text-slate-400 hover:text-white p-1"
              title="Help Center"
            >
              <HelpCircle size={16} />
            </button>
            <button
              data-testid="open-support-center-button"
              onClick={() => setSupportOpen(true)}
              className="text-slate-400 hover:text-white p-1"
              title="Contact Support"
            >
              <LifeBuoy size={16} />
            </button>
          </div>
          <div className="flex-1 min-h-0">{treeContent}</div>
          <button
            data-testid="collapse-tree-button"
            onClick={() => setTreeCollapsed(true)}
            title="Collapse model tree"
            className="absolute top-1/2 -right-3 -translate-y-1/2 z-10 h-8 w-6 rounded-full bg-slate-800 border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700 flex items-center justify-center transition-colors"
          >
            <ChevronLeft size={13} />
          </button>
        </div>
      )}
      {!isMobile && treeCollapsed && (
        <div className="w-8 flex-shrink-0 border-r border-slate-800 flex flex-col items-center pt-3">
          <button
            data-testid="expand-tree-button"
            onClick={() => setTreeCollapsed(false)}
            title="Show model tree"
            className="h-8 w-8 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 flex items-center justify-center transition-colors"
          >
            <PanelLeftOpen size={16} />
          </button>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {isMobile && (
          <div className="h-12 flex items-center gap-1 px-2 border-b border-slate-800 shrink-0" data-testid="mobile-workbench-header">
            <button data-testid="back-to-dashboard-button" onClick={() => navigate("/dashboard")} className="text-slate-400 hover:text-white p-2">
              <ArrowLeft size={16} />
            </button>
            <button data-testid="mobile-open-tree-button" onClick={() => setMobileTreeOpen(true)} className="text-slate-400 hover:text-white p-2">
              <Menu size={16} />
            </button>
            <span className="text-sm font-semibold truncate flex-1 px-1">{project.name}</span>
            <button
              data-testid="open-help-center-button"
              onClick={() => setHelpOpen(true)}
              className="text-slate-400 hover:text-white p-2"
              title="Help Center"
            >
              <HelpCircle size={16} />
            </button>
            <button
              data-testid="open-support-center-button"
              onClick={() => setSupportOpen(true)}
              className="text-slate-400 hover:text-white p-2"
              title="Contact Support"
            >
              <LifeBuoy size={16} />
            </button>
            <button data-testid="mobile-open-chat-button" onClick={() => setMobileChatOpen(true)} className="text-purple-300 hover:text-white p-2">
              <MessageSquareText size={16} />
            </button>
          </div>
        )}
        {isMobile && (
          <div
            data-testid="mobile-view-only-banner"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-amber-700 text-[11px] shrink-0"
          >
            <MonitorSmartphone size={12} className="shrink-0" />
            View-only on mobile - switch to desktop to edit this diagram
          </div>
        )}
        <div className="flex flex-col min-w-0 bg-white border-b border-slate-200" data-testid="workbench-toolbar">
          <div className="flex items-center h-10 px-2 min-w-0 bg-slate-50/80 border-b border-slate-200">
            {simpleViewMode ? (
              <div className="flex items-center gap-2 px-1" data-testid="simple-view-header">
                <Presentation size={15} className="text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Simple View</span>
                <span className="hidden sm:inline text-xs text-slate-400">Read-only stakeholder overview of the whole project</span>
              </div>
            ) : (
              <ViewsTabs
                views={views}
                activeViewId={activeViewId}
                onSelectView={setActiveViewId}
                onCreateView={handleCreateView}
                onRenameView={handleRenameView}
                onDeleteView={handleDeleteView}
              />
            )}
          </div>
          <div ref={toolbarOuterRowRef} className="relative flex items-center gap-1.5 px-3 py-2 min-w-0">
            {isMobile ? (
              <div className="flex items-center gap-2 overflow-x-auto">
                {!simpleViewMode && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="toggle-canvas-key-button"
                    onClick={() => setKeyVisible((v) => !v)}
                    title={keyVisible ? "Hide canvas key" : "Show canvas key"}
                    className="shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900"
                  >
                    {keyVisible ? <Eye size={13} className="mr-1.5" /> : <EyeOff size={13} className="mr-1.5" />}
                    Key
                  </Button>
                )}
                {simpleViewMode && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="download-simple-view-button"
                    onClick={handleDownloadSimpleView}
                    className="shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900"
                  >
                    <Download size={13} className="mr-1.5" />
                    Download PNG
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={simpleViewMode ? "default" : "outline"}
                  data-testid="toggle-simple-view-button"
                  onClick={handleToggleSimpleView}
                  disabled={simpleViewLoading}
                  className={
                    simpleViewMode
                      ? "shrink-0 bg-blue-600 hover:bg-blue-700"
                      : "shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900 disabled:text-slate-400 disabled:bg-slate-50"
                  }
                >
                  {simpleViewLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Presentation size={13} className="mr-1.5" />}
                  {simpleViewMode ? "Exit Simple View" : "Simple View"}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="mobile-more-actions-button"
                      disabled={reportGenerating}
                      className="shrink-0 bg-slate-50 border-slate-300 text-slate-700"
                    >
                      {reportGenerating ? <Loader2 size={13} className="animate-spin" /> : <MoreVertical size={14} />}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem data-testid="mobile-generate-report-menu-item" onClick={handleGenerateReport} disabled={reportGenerating}>
                      <FileText size={13} className="mr-2" /> Standard Report
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="mobile-generate-gap-report-menu-item" onClick={handleGenerateGapReport} disabled={reportGenerating}>
                      <ClipboardCheck size={13} className="mr-2" /> Gap Analysis
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="mobile-generate-hld-menu-item" onClick={handleGenerateHld} disabled={reportGenerating}>
                      <BookOpen size={13} className="mr-2" /> High-Level Design (HLD)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : simpleViewMode ? (
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="download-simple-view-button"
                  onClick={handleDownloadSimpleView}
                  className="shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900"
                >
                  <Download size={13} className="mr-1.5" />
                  Download PNG
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  data-testid="toggle-simple-view-button"
                  onClick={handleToggleSimpleView}
                  disabled={simpleViewLoading}
                  className="shrink-0 bg-blue-600 hover:bg-blue-700"
                >
                  {simpleViewLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Presentation size={13} className="mr-1.5" />}
                  Exit Simple View
                </Button>
                <div className="h-5 w-px bg-slate-200 mx-1 shrink-0" aria-hidden="true" />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="reports-menu-button"
                      disabled={reportGenerating}
                      className="shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900 disabled:text-slate-400 disabled:bg-slate-50"
                    >
                      {reportGenerating ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <FileText size={13} className="mr-1.5" />}
                      Reports
                      <ChevronDown size={13} className="ml-1.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem data-testid="generate-report-menu-item" onClick={handleGenerateReport} disabled={reportGenerating}>
                      <FileText size={13} className="mr-2" /> Standard Report
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="generate-gap-report-menu-item" onClick={handleGenerateGapReport} disabled={reportGenerating}>
                      <ClipboardCheck size={13} className="mr-2" /> Gap Analysis
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="generate-hld-menu-item" onClick={handleGenerateHld} disabled={reportGenerating}>
                      <BookOpen size={13} className="mr-2" /> High-Level Design (HLD)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <>
                <Button
                  ref={setToolbarItemRef("key")}
                  size="sm"
                  variant="outline"
                  data-testid="toggle-canvas-key-button"
                  onClick={() => setKeyVisible((v) => !v)}
                  title={keyVisible ? "Hide canvas key" : "Show canvas key"}
                  className="shrink-0 bg-slate-50 border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:text-slate-900"
                >
                  {keyVisible ? <Eye size={13} className={compactEdges ? "" : "mr-1.5"} /> : <EyeOff size={13} className={compactEdges ? "" : "mr-1.5"} />}
                  {!compactEdges && "Key"}
                </Button>
                <div className="h-5 w-px bg-slate-200 mx-1 shrink-0" aria-hidden="true" />
                <div ref={actionRowRef} className="flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden">
                  {toolbarFoldCandidates.map((item) => item.renderButton(hiddenToolbarIds.has(item.id)))}
                  <Button
                    ref={setToolbarItemRef("moreButtonClone")}
                    size="sm"
                    variant="outline"
                    aria-hidden="true"
                    tabIndex={-1}
                    className="invisible absolute pointer-events-none"
                  >
                    <MoreHorizontal size={14} />
                  </Button>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {hiddenToolbarIds.size > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="toolbar-more-menu-button"
                          title="More actions"
                          className="shrink-0 bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                        >
                          <MoreHorizontal size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {toolbarFoldCandidates.filter((item) => hiddenToolbarIds.has(item.id)).map((item) => item.renderMenuItem())}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <div className="h-5 w-px bg-slate-200 mx-1 shrink-0" aria-hidden="true" />
                  <Button
                    ref={setToolbarItemRef("element")}
                    size="sm"
                    data-testid="workbench-add-element-button"
                    title="Add Element"
                    onClick={() => {
                      setCreateElementLayer("business");
                      setCreateElementOpen(true);
                    }}
                    className="shrink-0 bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-600/25"
                  >
                    <Plus size={13} className={compactEdges ? "" : "mr-1.5"} />
                    {!compactEdges && "Element"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex-1 min-h-0">
          {simpleViewMode ? (
            <SimpleViewWrapper data={simpleViewData} />
          ) : activeView ? (
            <ArchiCanvasWrapper
              view={activeView}
              elementsById={elementsById}
              onNodeMove={handleNodeMove}
              onNodeClick={setSelectedElementId}
              onRemoveNode={handleRemoveNode}
              onConnectElements={handleConnectElements}
              onUpdateRelationship={handleUpdateRelationship}
              onDeleteRelationship={handleDeleteRelationship}
              onNodeResize={handleNodeResize}
              onNodeReparent={handleNodeReparent}
              onToggleCollapse={handleToggleCollapse}
              onSelectionChange={setSelectedNodeIds}
              onUngroup={handleUngroup}
              focusRequest={focusRequest}
              readOnly={isMobile}
              showKey={keyVisible}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">Create a view to start diagramming</div>
          )}
        </div>
      </div>

      {!isMobile && !chatCollapsed && (
        <div className="w-96 flex-shrink-0 border-l border-slate-800 relative">
          {chatContent}
          <button
            data-testid="collapse-chat-button"
            onClick={() => setChatCollapsed(true)}
            title="Collapse AI assistant"
            className="absolute top-1/2 -left-3 -translate-y-1/2 z-10 h-8 w-6 rounded-full bg-slate-800 border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700 flex items-center justify-center transition-colors"
          >
            <ChevronRight size={13} />
          </button>
        </div>
      )}
      {!isMobile && chatCollapsed && (
        <div className="w-8 flex-shrink-0 border-l border-slate-800 flex flex-col items-center pt-3">
          <button
            data-testid="expand-chat-button"
            onClick={() => setChatCollapsed(false)}
            title="Show AI assistant"
            className="h-8 w-8 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 flex items-center justify-center transition-colors"
          >
            <PanelRightOpen size={16} />
          </button>
        </div>
      )}

      {isMobile && (
        <Sheet open={mobileTreeOpen} onOpenChange={setMobileTreeOpen}>
          <SheetContent side="left" className="p-0 w-[85vw] max-w-sm border-slate-800" data-testid="mobile-tree-sheet">
            <SheetTitle className="sr-only">Model Tree</SheetTitle>
            {treeContent}
          </SheetContent>
        </Sheet>
      )}
      {isMobile && (
        <Sheet open={mobileChatOpen} onOpenChange={setMobileChatOpen}>
          <SheetContent side="right" className="p-0 w-[92vw] max-w-md" data-testid="mobile-chat-sheet">
            <SheetTitle className="sr-only">ModernTect Assistant</SheetTitle>
            {chatContent}
          </SheetContent>
        </Sheet>
      )}

      <CreateElementDialog open={createElementOpen} onOpenChange={setCreateElementOpen} defaultLayer={createElementLayer} onCreate={handleCreateElement} />
      <CreateRelationshipDialog
        open={!!pendingConnection}
        onOpenChange={(v) => !v && setPendingConnection(null)}
        sourceElement={pendingConnection?.source}
        targetElement={pendingConnection?.target}
        onCreate={handleCreateRelationship}
      />
      <ElementDetailsDialog
        open={!!selectedElement}
        onOpenChange={(v) => !v && setSelectedElementId(null)}
        element={selectedElement}
        relationships={selectedElementRelationships}
        elementsById={elementsById}
        owners={owners}
        onSave={handleSaveElement}
        onDelete={handleDeleteElement}
        onFocusElement={handleFocusElementOnCanvas}
      />
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl" data-testid="workbench-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Architecture</DialogTitle>
            <DialogDescription>Upload a .archimate file, or let AI draft a model from a document.</DialogDescription>
          </DialogHeader>
          <ImportPanel
            projectId={projectId}
            onComplete={async (viewId) => {
              setImportOpen(false);
              await Promise.all([loadElements(), loadRelationships(), loadViews(false)]);
              if (viewId) setActiveViewId(viewId);
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent data-testid="group-name-dialog">
          <DialogHeader>
            <DialogTitle>Group {selectedNodeIds.length} Elements</DialogTitle>
            <DialogDescription>Name this container. Grouped elements can be collapsed, resized, and moved together.</DialogDescription>
          </DialogHeader>
          <Input
            data-testid="group-name-input"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="e.g. Customer Channel"
            onKeyDown={(e) => e.key === "Enter" && handleGroupSelected()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialogOpen(false)} data-testid="group-name-cancel-button">
              Cancel
            </Button>
            <Button onClick={handleGroupSelected} data-testid="group-name-submit-button">
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <HelpCenter open={helpOpen} onOpenChange={setHelpOpen} />
      <SupportCenter open={supportOpen} onOpenChange={setSupportOpen} />
      <GroupSuggestionsDialog
        open={suggestionsOpen}
        onOpenChange={setSuggestionsOpen}
        suggestions={groupSuggestions}
        onAccept={handleAcceptSuggestion}
        onReject={handleRejectSuggestion}
        onAcceptAll={handleAcceptAllSuggestions}
      />
      {reportGenerating && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center"
          data-testid="report-generating-overlay"
        >
          <div className="bg-white rounded-xl px-8 py-6 flex flex-col items-center gap-3 shadow-2xl min-w-[280px]">
            <Loader2 className="animate-spin text-blue-600" size={28} />
            <p className="text-sm font-medium text-slate-700 text-center" data-testid="report-generating-status">
              {reportStep || "Generating report..."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
